import { gql } from '@apollo/client';
import * as React from 'react';
import * as uuid from 'uuid';
import { useNewExpenseMutationMutation } from './__generated__/CreateNewExpenseButton.generated';
import { SearchExpensesQueryQuery } from './__generated__/ExpensesPageHooks.generated';
import { useHotToast } from 'src/utils/useHotToast';
import { Button } from 'src/shadcn/components/ui/button';
import { Banknote, RotateCw } from 'lucide-react';

export interface ICreateNewExpenseButtonProps {
  onNewExpenseCreated: (
    expense: SearchExpensesQueryQuery['listExpenses']['items'][0]
  ) => void;
}

export const CreateNewExpenseButton = React.forwardRef<
  React.ElementRef<'div'>,
  ICreateNewExpenseButtonProps
>((props, ref) => {
  const { isSavingExpense, createNewExpense } = useUpsertExpense(props);
  return (
    <div
      onClick={() => createNewExpense()}
      id='add-new-expense'
      ref={ref}
      className='flex flex-row items-center'
    >
      <Banknote className='mr-2 h-4 w-4' />
      <span>New expense</span>
      {isSavingExpense && <RotateCw className='ml-2 h-4 w-4 animate-spin' />}
    </div>
  );
});

function useUpsertExpense(props: ICreateNewExpenseButtonProps) {
  const toast = useHotToast();
  const [upsertExpense, { loading: isUpsertingExpense, data: expenseData }] =
    useNewExpenseMutationMutation({
      onCompleted: e => {
        props.onNewExpenseCreated(e.upsertExpense);
      },
      onError: e => {
        toast.error(e.message);
      },
      fetchPolicy: 'network-only',
    });

  const createNewExpense = () => {
    upsertExpense({
      variables: {
        input: {
          id: uuid.v4(),
        },
      },
    });
  };

  return {
    createNewExpense,
    isSavingExpense: isUpsertingExpense,
    savedExpense: expenseData?.upsertExpense,
  };
}

const NEW_EXPENSE_MUTATION = gql`
  mutation NewExpenseMutation($input: ExpenseInput!) {
    upsertExpense(input: $input) {
      id
      createdAt
      expenseNumber
      updatedAt
      amount
      taxAmount
      paidAt
      paidTo
      category
      description
      paymentType
      images {
        url
        fileName
      }
      job {
        jobMetadata {
          id
          jobNumber
          id
          totalDue
          totalPaid
          timeCompleted
          createdAt
          type
          frontDeskComments
          mechanicComments
          estimateAmount
          jobNumber
          mechanicShopStatus
          status
        }
        customer {
          firstName
          lastName
          id
          email
          phoneNumber
        }
        car {
          id
          make
          model
          year
          colour
        }
        invoice {
          invoiceMetadata {
            id
            discount
            taxPercentage
            subtotal
          }
        }
        payments {
          id
          settledAmount
          status
          type
          squarePaymentId
          terminalCheckoutId
        }
      }
    }
  }
`;
